import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Info } from 'lucide-react';
import LegalDocModal from '@/components/LegalDocModal';

/**
 * Bloco de consentimento explícito para checkout / assinatura.
 *
 * - Carrega versões ATIVAS dos Termos do Cliente e da Política de Reembolso
 *   via `/api/admin/terms/active` (sem hardcode).
 * - Mostra checkbox obrigatório + links clicáveis (modal reutilizável).
 * - Exibe um resumo discreto da assinatura (recorrência mensal, cancelamento
 *   sem multa) — atende exigência de transparência (Pagar.me/Procon).
 *
 * Props:
 *   accepted: bool
 *   onAcceptedChange: (bool) => void
 *   onVersionsChange?: ({ terms, refund }) => void  // emitido após prefetch
 */
export default function CheckoutConsent({ accepted, onAcceptedChange, onVersionsChange }) {
  const [showTerms, setShowTerms] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchVersions = async () => {
      try {
        const [t, r] = await Promise.all([
          axios.get(`${API}/admin/terms/active`, { params: { type: 'client' } }).catch(() => null),
          axios.get(`${API}/admin/terms/active`, { params: { type: 'refund_policy' } }).catch(() => null),
        ]);
        if (!active) return;
        if (onVersionsChange) {
          onVersionsChange({
            terms: t?.data?.version || '1.0',
            refund: r?.data?.version || '1.0',
          });
        }
      } catch (e) {
        // mantém defaults
      }
    };
    fetchVersions();
    return () => { active = false; };
  }, [onVersionsChange]);

  return (
    <>
      <Card
        className="p-4 mb-6 bg-blue-50/40 border-blue-100"
        data-testid="checkout-consent"
      >
        <div className="flex items-start gap-3">
          <Checkbox
            id="checkout-consent-checkbox"
            checked={accepted}
            onCheckedChange={onAcceptedChange}
            data-testid="checkout-consent-checkbox"
          />
          <div className="flex-1">
            <label
              htmlFor="checkout-consent-checkbox"
              className="text-sm leading-relaxed cursor-pointer text-slate-700"
            >
              Li e concordo com os{' '}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTerms(true); }}
                className="text-primary hover:underline font-medium"
                data-testid="checkout-consent-terms-link"
              >
                Termos de Uso
              </button>
              {' '}e a{' '}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowRefund(true); }}
                className="text-primary hover:underline font-medium"
                data-testid="checkout-consent-refund-link"
              >
                Política de Reembolso
              </button>
              {' '}da JATOO.
            </label>

            <div className="flex items-start gap-2 mt-2 text-xs text-slate-500">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Cobrança recorrente mensal • Cancele quando quiser sem multa • Próxima cobrança 30 dias após a confirmação.
              </span>
            </div>
          </div>
        </div>
      </Card>

      <LegalDocModal
        type="client"
        open={showTerms}
        onOpenChange={setShowTerms}
        testIdPrefix="checkout-terms"
      />
      <LegalDocModal
        type="refund_policy"
        open={showRefund}
        onOpenChange={setShowRefund}
        testIdPrefix="checkout-refund"
      />
    </>
  );
}
